import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS, scrollToId } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="header-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-[#FBF9F5]/90 backdrop-blur-md border-[#E2E8F0]/80 shadow-[0_8px_30px_rgba(11,25,44,0.06)]"
          : "bg-[#FBF9F5]/70 backdrop-blur-sm border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <button
          data-testid="nav-logo-button"
          onClick={() => go("home")}
          className="flex items-center gap-3.5 group"
          aria-label="Trivena Exim LLP home"
        >
          <span className="bg-[#FBF9F5] rounded-lg px-2.5 py-1.5 shadow-[0_4px_16px_rgba(11,25,44,0.08)]">
            <img
              src={LOGO_URL}
              alt="Trivena Exim LLP logo"
              className="h-12 md:h-14 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => go(link.id)}
              className="relative text-[13px] font-medium tracking-wide text-[#2C3E55] hover:text-[#0B192C] transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#C5A059] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2.5">
            <button
              data-testid="header-services-button"
              onClick={() => go("services")}
              className="inline-flex items-center rounded-full bg-[#C5A059] text-[#0B192C] text-[13px] font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300 hover:bg-[#0B192C] hover:text-[#FBF9F5]"
            >
              Our Services
            </button>
            <button
              data-testid="header-contact-button"
              onClick={() => go("contact")}
              className="inline-flex items-center rounded-full border border-[#0B192C]/30 text-[#0B192C] text-[13px] font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300 hover:border-[#C5A059] hover:text-[#B38C44]"
            >
              Contact Us
            </button>
          </div>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-[#0B192C]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#FBF9F5]/95 backdrop-blur-md border-t border-[#E2E8F0]/80"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`nav-mobile-link-${link.id}`}
                  onClick={() => go(link.id)}
                  className="text-left font-serif text-2xl text-[#0B192C]"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
